import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { subscriptionApi } from '../utils/apiUtils';

const SubscriptionManagement = () => {
	const { user } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const handlePortalSession = async () => {
		try {
			setLoading(true);
			const { url } = await subscriptionApi.createPortalSession();
			window.location.href = url;
		} catch (err) {
			setError('Failed to access subscription management.');
			console.error('Portal session error:', err);
		} finally {
			setLoading(false);
		}
	};

	if (!user) return null;

	// The free plan is the floor — while the server is still reconciling a
	// brand-new user we render them as free_plan/active so the UI never shows
	// an empty "None / None" state.
	const effectiveTier = user.stripeSubscriptionTier || 'free_plan';
	const effectiveStatus = user.stripeSubscriptionStatus || 'active';
	const isFreePlan = effectiveTier === 'free_plan';

	const rows = [
		{ label: 'Status', value: effectiveStatus, color: effectiveStatus === 'active' ? 'text-emerald-600' : 'text-amber-600' },
		{ label: 'Plan', value: effectiveTier },
		user.stripeTrialEndsDate && { label: 'Trial Ends', value: format(new Date(user.stripeTrialEndsDate), 'MMM dd, yyyy') },
		user.stripeCurrentPeriodEndDate && !isFreePlan && { label: 'Next Billing', value: format(new Date(user.stripeCurrentPeriodEndDate), 'MMM dd, yyyy') },
		user.stripeCancelAtPeriodEnd && { label: 'Cancellation', value: 'Cancels at period end', color: 'text-red-500' },
	].filter(Boolean);

	const formatLabel = (val) => val ? val.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'None';

	return (
		<div className="max-w-lg mx-auto space-y-4">
			<div className="bg-gray-50 rounded-lg p-4 space-y-3">
				{rows.map((row, i) => (
					<div key={i} className="flex justify-between items-center">
						<span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">{row.label}</span>
						<span className={`text-xs font-medium ${row.color || 'text-gray-900'}`}>
							{row.label === 'Status' || row.label === 'Plan' ? formatLabel(row.value) : row.value}
						</span>
					</div>
				))}
				{user.stripeLatestInvoiceUrl && (
					<div className="flex justify-between items-center">
						<span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">Invoice</span>
						<a href={user.stripeLatestInvoiceUrl} target="_blank" rel="noopener noreferrer"
							className="text-xs text-purple-600 hover:text-purple-700 font-medium">
							View Latest
						</a>
					</div>
				)}
			</div>

			<button
				onClick={handlePortalSession}
				disabled={loading}
				className={`w-full py-2.5 px-4 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
					loading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white shadow-glow-purple'
				}`}
			>
				{loading ? 'Loading...' : 'Manage Subscription'}
			</button>

			{error && <p className="text-red-500 text-xs">{error}</p>}
		</div>
	);
};

export default SubscriptionManagement;
